import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  emailHubService,
  type InboundEmail,
  type ContactFormSubmission,
  type EmailStats,
  type GetEmailsParams,
  type GetContactFormsParams,
  type EmailStatus,
  type EmailPriority,
  type PaginatedResponse,
} from '../services/emailHubService';
import ProtectedRoute from '../components/auth/ProtectedRoute';
import EmailList, { type EmailListItem } from '../components/email-hub/EmailList';
import EmailDetail from '../components/email-hub/EmailDetail';
import EmailFilters, { type EmailFiltersValue } from '../components/email-hub/EmailFilters';
import './AdminEmailHubPage.css';

type ActiveTab = 'emails' | 'contact_forms';

const PAGE_SIZE = 20;

/**
 * Contact-form statuses on the backend (`new | read | replied | archived`)
 * are a strict subset of inbound-email statuses — contact forms have no
 * notion of `spam` / `auto_replied`. When the user picks one of those in the
 * shared filter, the contact-forms request must skip the status param (or
 * be skipped entirely) to avoid a 400 from class-validator @IsEnum.
 */
const CONTACT_FORM_STATUSES: ReadonlyArray<string> = ['new', 'read', 'replied', 'archived'];
const isContactFormStatus = (s: string | undefined): s is 'new' | 'read' | 'replied' | 'archived' =>
    !!s && CONTACT_FORM_STATUSES.includes(s);

const AdminEmailHubPageInner: React.FC = () => {
  const { t } = useTranslation(['emailHub', 'common']);
  const navigate = useNavigate();

  // ─── Tab ──────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<ActiveTab>('emails');

  // ─── Stats ────────────────────────────────────────────
  const [stats, setStats] = useState<EmailStats | null>(null);

  // ─── Filters ──────────────────────────────────────────
  const [filters, setFilters] = useState<EmailFiltersValue>({});

  // ─── Emails list ──────────────────────────────────────
  const [emailsPage, setEmailsPage] = useState(1);
  const [emailsResult, setEmailsResult] = useState<PaginatedResponse<InboundEmail> | null>(null);
  const [emailsLoading, setEmailsLoading] = useState(false);

  // ─── Contact forms list ───────────────────────────────
  const [formsPage, setFormsPage] = useState(1);
  const [formsResult, setFormsResult] = useState<PaginatedResponse<ContactFormSubmission> | null>(null);
  const [formsLoading, setFormsLoading] = useState(false);

  // ─── Selected item ────────────────────────────────────
  const [selectedItem, setSelectedItem] = useState<EmailListItem | null>(null);
  const [replying, setReplying] = useState(false);

  // ─── Error ────────────────────────────────────────────
  const [error, setError] = useState<string | null>(null);

  // ─── Fetch stats ──────────────────────────────────────
  const fetchStats = useCallback(async () => {
    try {
      const data = await emailHubService.getStats();
      setStats(data);
    } catch {
      // Non-critical, swallow silently
    }
  }, []);

  // ─── Fetch emails (unified: inbound + contact forms) ──
  const fetchEmails = useCallback(async (f: EmailFiltersValue, page: number) => {
    setEmailsLoading(true);
    setError(null);
    try {
      // Build params with real page and PAGE_SIZE for server-side pagination
      const emailParams: GetEmailsParams = {
        ...(f.status ? { status: f.status } : {}),
        ...(f.priority ? { priority: f.priority } : {}),
        ...(f.intent ? { intent: f.intent } : {}),
        ...(f.mailbox ? { mailbox: f.mailbox } : {}),
        ...(f.search ? { search: f.search } : {}),
        page,
        limit: PAGE_SIZE,
      };
      const cfParams: GetContactFormsParams = {
        ...(isContactFormStatus(f.status) ? { status: f.status } : {}),
        ...(f.search ? { search: f.search } : {}),
        page,
        limit: PAGE_SIZE,
      };
      // Skip contact forms when the filter is email-only (mailbox, or a
      // status like `spam` / `auto_replied` that contact forms never have).
      const hasMailboxFilter = !!f.mailbox;
      const hasEmailOnlyStatus = !!f.status && !isContactFormStatus(f.status);
      const skipContactForms = hasMailboxFilter || hasEmailOnlyStatus;
      const [emailsRes, formsRes] = await Promise.all([
        emailHubService.getEmails(emailParams),
        skipContactForms
          ? Promise.resolve({ items: [], total: 0, page: 1, limit: PAGE_SIZE, totalPages: 0 })
          : emailHubService.getContactForms(cfParams),
      ]);

      // Merge current page results and sort by createdAt descending
      const merged: EmailListItem[] = [
        ...emailsRes.items,
        ...formsRes.items,
      ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

      // Use the larger totalPages from both sources (server-side pagination)
      const combinedTotal = emailsRes.total + formsRes.total;
      const combinedTotalPages = Math.ceil(combinedTotal / PAGE_SIZE);

      setEmailsResult({
        items: merged as unknown as InboundEmail[],
        total: combinedTotal,
        page,
        limit: PAGE_SIZE,
        totalPages: combinedTotalPages,
      });
    } catch {
      setError(t('emailHub:error.loadFailed', 'Failed to load emails'));
    } finally {
      setEmailsLoading(false);
    }
  }, [t]);

  // ─── Fetch contact forms ──────────────────────────────
  const fetchForms = useCallback(async (f: EmailFiltersValue, page: number) => {
    setFormsLoading(true);
    setError(null);
    try {
      // `spam` / `auto_replied` don't exist on contact forms — drop them
      // instead of sending and getting a 400.
      const params: GetContactFormsParams = {
        ...(isContactFormStatus(f.status) ? { status: f.status } : {}),
        ...(f.category ? { category: f.category } : {}),
        ...(f.search ? { search: f.search } : {}),
        page,
        limit: PAGE_SIZE,
      };
      const result = await emailHubService.getContactForms(params);
      setFormsResult(result);
    } catch {
      setError(t('emailHub:error.loadFailed', 'Failed to load contact forms'));
    } finally {
      setFormsLoading(false);
    }
  }, [t]);

  // ─── Initial load & on filter/tab/page change ─────────
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (activeTab === 'emails') {
      fetchEmails(filters, emailsPage);
    }
  }, [activeTab, filters, emailsPage, fetchEmails]);

  useEffect(() => {
    if (activeTab === 'contact_forms') {
      fetchForms(filters, formsPage);
    }
  }, [activeTab, filters, formsPage, fetchForms]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setEmailsPage(1);
    setFormsPage(1);
    setSelectedItem(null);
  }, [filters]);

  // Reset selected when tab changes
  useEffect(() => {
    setSelectedItem(null);
    setFilters({});
  }, [activeTab]);

  // ─── Handlers ─────────────────────────────────────────
  const handleFiltersChange = (f: EmailFiltersValue) => {
    setFilters(f);
  };

  const handleSelect = (item: EmailListItem) => {
    setSelectedItem(item);

    // Auto-mark NEW → READ when the admin opens an item. Skip if already read
    // (or in any later state like REPLIED/ARCHIVED/SPAM) so we never downgrade
    // a manually-set status. Errors are swallowed — the read marker is a
    // convenience, not load-bearing.
    if (item.status !== 'new') return;
    const isEmail = 'fromAddress' in item;
    const markPromise = isEmail
      ? emailHubService.updateEmailStatus(item.id, 'read')
      : emailHubService.updateContactFormStatus(item.id, 'read');

    markPromise
      .then((updated) => {
        // Patch only the row that just changed — re-fetching the whole page
        // makes the list re-render and the user lose focus context. Stats
        // (unread/new counters) still need a refresh because the totals
        // dropped by one.
        setSelectedItem(updated as EmailListItem);
        if (isEmail) {
          setEmailsResult((prev) => prev
            ? {
                ...prev,
                items: prev.items.map((it) =>
                  it.id === updated.id ? (updated as InboundEmail) : it,
                ),
              }
            : prev,
          );
        } else {
          setFormsResult((prev) => prev
            ? {
                ...prev,
                items: prev.items.map((it) =>
                  it.id === updated.id ? (updated as ContactFormSubmission) : it,
                ),
              }
            : prev,
          );
        }
        fetchStats();
      })
      .catch(() => { /* silent — selection still works */ });
  };

  const handleReply = async (body: string) => {
    if (!selectedItem) return;
    setReplying(true);
    try {
      const isEmail = 'fromAddress' in selectedItem;
      if (isEmail) {
        await emailHubService.replyToEmail(selectedItem.id, { body });
      } else {
        await emailHubService.replyToContactForm(selectedItem.id, { body });
      }
      // Refresh
      if (activeTab === 'emails') {
        await fetchEmails(filters, emailsPage);
      } else {
        await fetchForms(filters, formsPage);
      }
      await fetchStats();
    } finally {
      setReplying(false);
    }
  };

  const handleUpdateStatus = async (status: EmailStatus) => {
    if (!selectedItem) return;
    try {
      if (activeTab === 'emails') {
        const updated = await emailHubService.updateEmailStatus(selectedItem.id, status);
        setSelectedItem(updated);
        await fetchEmails(filters, emailsPage);
        await fetchStats();
      } else {
        const updated = await emailHubService.updateContactFormStatus(selectedItem.id, status as ContactFormSubmission['status']);
        setSelectedItem(updated);
        await fetchForms(filters, formsPage);
      }
    } catch {
      setError(t('emailHub:error.updateFailed', 'Failed to update status'));
    }
  };

  const handleUpdatePriority = async (priority: EmailPriority) => {
    if (!selectedItem) return;
    try {
      if (activeTab === 'emails') {
        const updated = await emailHubService.updateEmailPriority(selectedItem.id, priority);
        setSelectedItem(updated);
        await fetchEmails(filters, emailsPage);
      }
    } catch {
      setError(t('emailHub:error.updateFailed', 'Failed to update priority'));
    }
  };

  const handleRefresh = () => {
    fetchStats();
    if (activeTab === 'emails') {
      fetchEmails(filters, emailsPage);
    } else {
      fetchForms(filters, formsPage);
    }
  };

  const [markingAllRead, setMarkingAllRead] = useState(false);

  const handleMarkAllRead = async () => {
    if (markingAllRead) return;
    setMarkingAllRead(true);
    try {
      await emailHubService.markAllAsRead();
      // Refresh both lists + stats; the active list's NEW dots should clear.
      await Promise.all([
        fetchEmails(filters, emailsPage),
        fetchForms(filters, formsPage),
        fetchStats(),
      ]);
    } catch {
      setError(t('emailHub:error.updateFailed', 'Failed to mark all as read'));
    } finally {
      setMarkingAllRead(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedItem) return;
    const isEmail = 'fromAddress' in selectedItem;
    if (isEmail) {
      await emailHubService.deleteEmail(selectedItem.id);
    } else {
      await emailHubService.deleteContactForm(selectedItem.id);
    }
    setSelectedItem(null);
    if (activeTab === 'emails') {
      await fetchEmails(filters, emailsPage);
    } else {
      await fetchForms(filters, formsPage);
    }
    await fetchStats();
  };

  // ─── Derived ──────────────────────────────────────────
  const currentItems: EmailListItem[] =
    activeTab === 'emails'
      ? (emailsResult?.items ?? [])
      : (formsResult?.items ?? []);

  const currentLoading = activeTab === 'emails' ? emailsLoading : formsLoading;

  const currentPage = activeTab === 'emails' ? emailsPage : formsPage;
  const setCurrentPage = activeTab === 'emails' ? setEmailsPage : setFormsPage;
  const totalPages =
    activeTab === 'emails'
      ? (emailsResult?.totalPages ?? 1)
      : (formsResult?.totalPages ?? 1);
  const totalItems =
    activeTab === 'emails'
      ? (emailsResult?.total ?? 0)
      : (formsResult?.total ?? 0);

  // ─── Render ───────────────────────────────────────────
  return (
    <div className="email-hub-page">
      {/* Header */}
      <div className="email-hub-header">
        <button className="email-hub-back" onClick={() => navigate(-1)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
        <h1>{t('emailHub:title', 'Email Hub')}</h1>
        <div className="email-hub-header-actions">
          <button
            className="email-hub-header-mark-all"
            onClick={handleMarkAllRead}
            disabled={markingAllRead || !stats || stats.new === 0}
            title={t('emailHub:markAllRead.tooltip', 'Mark every NEW email and contact form as read')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 12l5 5L22 4" />
              <path d="M2 12l5 5" opacity="0.4" transform="translate(7 0)" />
            </svg>
            {markingAllRead
              ? t('emailHub:markAllRead.working', '標記中…')
              : t('emailHub:markAllRead.label', '全部標為已讀')}
          </button>
          <button className="email-hub-header-refresh" onClick={handleRefresh}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 2v6h-6" />
              <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
              <path d="M3 22v-6h6" />
              <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
            </svg>
            {t('common:refresh', 'Refresh')}
          </button>
        </div>
      </div>

      {/* Error strip */}
      {error && (
        <div className="email-hub-error" role="alert">
          {error}
        </div>
      )}

      {/* Stats dashboard */}
      {stats && (
        <div className="email-hub-stats" aria-label={t('emailHub:stats.label', 'Email statistics')}>
          <div className="email-hub-stat-card">
            <div className="email-hub-stat-card__value">{stats.total.toLocaleString()}</div>
            <div className="email-hub-stat-card__label">{t('emailHub:stats.total', 'Total')}</div>
          </div>
          <div className="email-hub-stat-card email-hub-stat-card--new">
            <div className="email-hub-stat-card__value">{stats.new.toLocaleString()}</div>
            <div className="email-hub-stat-card__label">{t('emailHub:stats.new', 'New')}</div>
          </div>
          <div className="email-hub-stat-card email-hub-stat-card--unread">
            <div className="email-hub-stat-card__value">{stats.unread.toLocaleString()}</div>
            <div className="email-hub-stat-card__label">{t('emailHub:stats.unread', 'Unread')}</div>
          </div>
          <div className="email-hub-stat-card email-hub-stat-card--today">
            <div className="email-hub-stat-card__value">{stats.today.toLocaleString()}</div>
            <div className="email-hub-stat-card__label">{t('emailHub:stats.today', 'Today')}</div>
          </div>
          <div className="email-hub-stat-card email-hub-stat-card--spam">
            <div className="email-hub-stat-card__value">{stats.spam.toLocaleString()}</div>
            <div className="email-hub-stat-card__label">{t('emailHub:stats.spam', 'Spam')}</div>
          </div>
        </div>
      )}

      {/* Tab switcher */}
      <div className="email-hub-tabs" role="tablist">
        <button
          role="tab"
          aria-selected={activeTab === 'emails'}
          className={`email-hub-tab ${activeTab === 'emails' ? 'active' : ''}`}
          onClick={() => setActiveTab('emails')}
        >
          {t('emailHub:tabs.emails', 'Inbound Emails')}
          {stats && stats.new > 0 && activeTab !== 'emails' && (
            <span className="email-hub-status-badge email-hub-status-badge--new" style={{ marginLeft: 6 }}>
              {stats.new}
            </span>
          )}
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'contact_forms'}
          className={`email-hub-tab ${activeTab === 'contact_forms' ? 'active' : ''}`}
          onClick={() => setActiveTab('contact_forms')}
        >
          {t('emailHub:tabs.contactForms', 'Contact Forms')}
        </button>
      </div>

      {/* Main body: list + detail */}
      <div className="email-hub-body">
        {/* Left: filter + list */}
        <div className="email-hub-panel">
          <EmailFilters filters={filters} onChange={handleFiltersChange} activeTab={activeTab} />
          <EmailList
            items={currentItems}
            onSelect={handleSelect}
            selectedId={selectedItem?.id}
            loading={currentLoading}
            emptyMessage={t('emailHub:list.empty', 'No messages found')}
          />
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="email-hub-pagination">
              <button
                className="email-hub-pagination__btn"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
              >
                {t('common:prev', 'Prev')}
              </button>
              <span className="email-hub-pagination__info">
                {t('emailHub:pagination.info', '{{page}} / {{total}}', {
                  page: currentPage,
                  total: totalPages,
                })}
                {' '}
                <span style={{ color: 'var(--color-text-light)' }}>
                  ({totalItems.toLocaleString()} {t('emailHub:pagination.items', 'items')})
                </span>
              </span>
              <button
                className="email-hub-pagination__btn"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
              >
                {t('common:next', 'Next')}
              </button>
            </div>
          )}
        </div>

        {/* Right: detail */}
        <div className="email-hub-panel">
          {selectedItem ? (
            <EmailDetail
              item={selectedItem}
              onReply={handleReply}
              onUpdateStatus={handleUpdateStatus}
              onUpdatePriority={handleUpdatePriority}
              onDelete={handleDelete}
              onClose={() => setSelectedItem(null)}
              replying={replying}
            />
          ) : (
            <div className="email-hub-detail-empty">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <p>{t('emailHub:detail.selectPrompt', 'Select a message to view details')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Wrap in ProtectedRoute with admin role
const AdminEmailHubPage: React.FC = () => (
  <ProtectedRoute requiredRoles={['admin']}>
    <AdminEmailHubPageInner />
  </ProtectedRoute>
);

export default AdminEmailHubPage;
